import PropTypes from "prop-types";
import React from "react";
import ReactDOM from "react-dom";
import _ from "lodash";
import { Power2, TimelineMax, TweenMax } from "gsap";

import AddDestination from "./AddDestination";

/**
 * -Overlay-
 * Overlay component
 **/
export default class Overlay extends React.Component {
  state = {
    shown: false,
  };

  centrePosition = () => {
    // Centre marker in window on each select
    const { map } = this.props;
    let intended_position;
    if (this.props.marker) {
      intended_position = this.props.marker.getPosition();
    }
    map.setCenter(intended_position);
  };

  setPosition = () => {
    const { google } = this.props;
    const overlayProjection = this.overlay.getProjection();
    let tl,
      intended_position,
      vertical_offset = 0,
      arrow_height = 16;

    // From Marker
    if (this.props.marker) {
      intended_position = this.props.marker.getPosition();
      const marker_anchor = this.props.marker.anchorPoint;
      vertical_offset = marker_anchor.y;

      // Position plus anchor (offset)
      tl = overlayProjection.fromLatLngToDivPixel(intended_position);
      tl.y +=
        vertical_offset - this.overlayContainer.offsetHeight / 2 - arrow_height;

      // From position (rightclick)
    } else if (this.props.position) {
      intended_position = new google.maps.LatLng(this.props.position);
      tl = overlayProjection.fromLatLngToDivPixel(intended_position);
      tl.y -= this.overlayContainer.offsetHeight / 2 + arrow_height;
    }

    this.overlayContainer.style.left = tl.x + "px";
    this.overlayContainer.style.top = tl.y + "px";
  };

  offsetCenter = (latlng, offsetx, offsety) => {
    const { map, google } = this.props;

    // latlng is the apparent centre-point
    // offsetx is the distance you want that point to move to the right, in pixels
    // offsety is the distance you want that point to move upwards, in pixels
    // offset can be negative
    const scale = Math.pow(2, map.getZoom());

    const worldCoordinateCenter = map.getProjection().fromLatLngToPoint(latlng);
    const pixelOffset = new google.maps.Point(
      offsetx / scale || 0,
      offsety / scale || 0
    );

    const worldCoordinateNewCenter = new google.maps.Point(
      worldCoordinateCenter.x - pixelOffset.x,
      worldCoordinateCenter.y + pixelOffset.y
    );

    const newCenter = map
      .getProjection()
      .fromPointToLatLng(worldCoordinateNewCenter);

    map.setCenter(newCenter);
  };

  showOverlay = () => {
    this.renderOverlay();
    // Show
    return new Promise((resolve, reject) => {
      TweenMax.killChildTweensOf(this.overlayContainer);

      const overlayIn = new TimelineMax({
        onComplete: () => {
          this.setState({
            shown: true,
          });
          resolve(this);
        },
      });
      overlayIn.fromTo(
        this.overlayContainer,
        0.4,
        { autoAlpha: 0 },
        { autoAlpha: 1, ease: Power2.easeInOut }
      );
    });
  };

  hideOverlay = () => {
    // Resolve if already, or when, hidden
    return new Promise((resolve, reject) => {
      if (!this.overlayContainer) {
        resolve(this);
      }
      
      const isHidden =
        this.overlayContainer.style &&
        this.overlayContainer.style.opacity === "0"
          ? true
          : false;

      if (isHidden) {
        resolve(this);
      } else {
        TweenMax.killChildTweensOf(this.overlayContainer);

        const overlayOut = new TimelineMax({
          onComplete: () => {
            resolve(this);
          },
        });

        overlayOut.fromTo(
          this.overlayContainer,
          0.25,
          { autoAlpha: 1 },
          {
            autoAlpha: 0,
            ease: Power2.easeInOut,
          }
        );
      }
    });
  };

  addOverlay = () => {
    const { map, google } = this.props;
  
    this.overlay = new google.maps.OverlayView();
    
    this.overlay.setMap(map);

    this.overlay.onAdd = () => {
      const div = document.createElement("div");
      div.classList.add("map-overlay");
      this.overlayContainer = div;
    };

    this.overlay.draw = () => {
      const panes = this.overlay.getPanes();
      panes.floatPane.appendChild(this.overlayContainer);

      // Show in draw to get dimensions
      if (this.props.visible === true && this.state.shown === false) {
        this.showOverlay();
      }

      if (this.props.visible === true) {
        this.setPosition();
      }
    };

    this.overlay.onRemove = () => {
      const elem = this.overlayContainer;
      elem.parentNode.removeChild(elem);
    };
  };

  closeOverlay = () => {
    this.hideOverlay().then(() => {
      this.setState({ shown: false });
      this.overlay.setMap(null);
    });
  };

  renderOverlay = () => {
    if (!this.overlayContainer) {
      return null;      
    }

    ReactDOM.render(
      <OverlayContent
        content={this.props.content}
        marker={this.props.marker}
        position={this.props.position}
        title={this.props.title}
        onRoad={this.props.onRoad}
        routeNo={this.props.routeNo}
        color={this.props.color}
        area={this.props.routeArea}
        closeOverlay={this.closeOverlay}
        {...this.props}
      />,
      this.overlayContainer
    );
  };

  componentDidMount() {
    this.addOverlay();
    this.centrePosition();
  }

  componentDidUpdate(prevProps) {
    // Re-render on new location due do render clone requiring latest props
    const newLocations = !_.isEqual(this.props.locations, prevProps.locations);
    if (newLocations) {
      this.renderOverlay();
    }

    // Visibility
    if (this.props.visible !== prevProps.visible) {
      if (this.props.visible === true) {
        this.showOverlay();
      } else {
        this.hideOverlay().then(() => this.setState({ shown: false }));
      }
    }
  }

  componentWillUnmount() {
    if (this.overlay) {
      this.hideOverlay().then(() => {
        this.overlay.setMap(null);
      });
    }
  }

  render() {
    return null;
  }
}

/**
 * -OverlayContent-
 * OverlayContent component
 **/
class OverlayContent extends React.Component {
  handleToggleStreetView = (e) => {
    e.preventDefault();
    const position = this.props.marker.getPosition();
    const lat = position.lat();
    const lng = position.lng();
    const { map } = this.props;
    const pin = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };

    let panorama = map.getStreetView(); // TODO fix type
    panorama.setPosition(pin);
    panorama.setPov(
      /** @type {google.maps.StreetViewPov} */ {
        heading: 265,
        pitch: 0,
      },
    );
    
    const toggle = panorama.getVisible();


    if (toggle === false) {
      panorama.setVisible(true);
    } else {
      panorama.setVisible(false);
    }
  }

  render() {
    const routeContent = this.props.content;
    const routeTitle = this.props.title;
    const routeColor = this.props.routeColor;

    const routeRoadHtml = `<p className="on-road">${this.props.onRoad}</p>`;
    const area = this.props.area;

    let innerHtml;
    let routeTitleHtml;
    
    // Prepare HTML segment for regional & national routes.
    if (area === "national") {
      routeTitleHtml = `<h4 className="route-icon-block">
				${
          this.props.routeNo
            ? `<span style="background-color: ${routeColor}">${this.props.routeNo}</span> `
            : ``
        }
			${routeTitle}</h4>`;

      if (routeTitle) {
        innerHtml = routeTitleHtml.concat(routeRoadHtml);
      } else {
        innerHtml = routeRoadHtml;
      }
    } else if (area === "regional") {
      routeTitleHtml = `<h3 className="signpost"><span className="signpost-name">Cycle Route</span></h3>
			<h4 className="signpost-title"><span className="signpost-colour" style="background-color: ${routeColor}"></span>${routeTitle}</h4>`;

      innerHtml = routeTitleHtml;
    }

    return (
      <div className="map-overlay-container">
        <div className="map-overlay-arrow"></div>
        <a className="map-overlay-close" onClick={this.props.closeOverlay}>
          <span className="assistive-text">Close overlay</span>
          <svg className="icon" aria-hidden="true">
            <use xlinkHref="#close" />
          </svg>
        </a>

        {routeTitle ? (
          <div className="regional">
            <div
              className="map-overlay-content user-content"
              dangerouslySetInnerHTML={{ __html: innerHtml }}
            />
          </div>
        ) : (
          <div>
            <div
              className="user-content"
              dangerouslySetInnerHTML={{ __html: routeContent }}
            />
            {this.props.marker !== undefined && <a className="toggle-streetview" onClick={this.handleToggleStreetView}>Street View</a>}
            <AddDestination
              marker={this.props.marker}
              position={this.props.position}
              title={this.props.title}
              {...this.props}
            />
          </div>
        )}
      </div>
    );
  }
}

Overlay.propTypes = {
  content: PropTypes.string,
  shown: PropTypes.bool,
  marker: PropTypes.object,
  position: PropTypes.object,
  title: PropTypes.string,
};

Overlay.defaultProps = {
  visible: true,
};

Overlay.contextTypes = {
  router: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/googlemaps/Overlay.js